/** Die Trainingswoche (Mo-So): geplante Einheiten mit Status je Tag - fuer Bereichsseiten (F-34 E2, BE-03). */
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { geplanteEinheiten, laeufe, planerfuellung, umfang, type GeplanteEinheit } from '@/domain/training/auswertung';
import type { LocalDate } from '@/domain/types';
import { addDays, endOfWeek, startOfWeek } from '@/lib/datetime';

export type TagStatus = 'frei' | 'erledigt' | 'teilweise' | 'ausgefallen' | 'offen';

export interface WochenTag {
  datum: LocalDate;
  einheiten: (GeplanteEinheit & { erledigt: boolean })[];
  /** Fertige Aktivitaeten ohne Plan (frei eingetragen) an diesem Tag. */
  frei: number;
  status: TagStatus;
}

export function tagStatus(einheiten: { erledigt: boolean }[], frei: number, datum: LocalDate, heute: LocalDate): TagStatus {
  const fertig = einheiten.filter((e) => e.erledigt).length;
  if (einheiten.length === 0) return frei > 0 ? 'erledigt' : 'frei';
  if (fertig === einheiten.length) return 'erledigt';
  if (datum < heute) return fertig > 0 ? 'teilweise' : 'ausgefallen';
  return fertig > 0 ? 'teilweise' : 'offen';
}

export function useTrainingWoche(heute: LocalDate) {
  return useLiveQuery(async () => {
    const von = startOfWeek(heute);
    const bis = endOfWeek(heute);
    const [plaene, einheiten, protokolle, einstellungen] = await Promise.all([
      db.trainingsplaene.toArray(), db.planEinheiten.toArray(), db.trainingsprotokolle.where('datum').between(von, bis, true, true).toArray(),
      db.trainingsEinstellungen.get('standard'),
    ]);
    const fertige = protokolle.filter((p) => p.status === 'fertig');
    const geplant = geplanteEinheiten(plaene, einheiten, von, bis);
    const erledigt = new Set(fertige.filter((p) => p.planEinheitId).map((p) => `${p.datum}|${p.planEinheitId}`));
    const tage: WochenTag[] = Array.from({ length: 7 }, (_, i) => {
      const datum = addDays(von, i);
      const ee = geplant.filter((g) => g.datum === datum).map((g) => ({ ...g, erledigt: erledigt.has(`${g.datum}|${g.einheit.id}`) }));
      const frei = fertige.filter((p) => p.datum === datum && !p.planEinheitId).length;
      return { datum, einheiten: ee, frei, status: tagStatus(ee, frei, datum, heute) };
    });
    return {
      tage, einstellungen,
      geplant: geplant.length, erledigt: geplant.filter((g) => erledigt.has(`${g.datum}|${g.einheit.id}`)).length,
      ausgefallen: planerfuellung(geplant, protokolle, heute).ausgefallen.length,
      umfang: umfang(protokolle, von, bis),
      km: laeufe(fertige).reduce((s, l) => s + l.km, 0),
    };
  }, [heute]);
}
